import React, { useState, useEffect } from 'react';
import { FaTimes, FaCalendarAlt, FaNewspaper, FaEye, FaChevronRight } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import api, { BASE_URL } from '../../../../utils/api';
import './ViewNews.css';

const API_BASE = BASE_URL;

const ViewNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const location = useLocation();

  const loadNews = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/news', { params: { limit: 100 } });
      const list = data.data || [];
      setNewsList(list);
      // Auto-open news if navigated from notification
      const openId = location.state?.openNewsId;
      if (openId) {
        const found = list.find(n => String(n._id) === String(openId));
        if (found) openNews(found);
      }
    } catch (error) {
      console.error('Failed to load news:', error);
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const getImageUrl = (news) => {
    if (!news.image) return null;
    if (news.image.startsWith('http')) return news.image;
    let imagePath = news.image.replace(/\\/g, '/');
    if (imagePath.startsWith('uploads/')) {
      return `${API_BASE}/${imagePath}`;
    }
    return `${API_BASE}/uploads/${imagePath}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const openNews = (news) => {
    setSelectedNews(news);
    // Prevent background scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeNews = () => {
    setSelectedNews(null);
    // Restore background scrolling
    document.body.style.overflow = 'auto';
  };

  const latestNews = newsList.length > 0 ? newsList[0] : null;

  return (
    <div className="view-news-container">
      <h1 className="view-news-page-heading">
        <FaNewspaper className="view-news-heading-icon" />
        Latest News & Updates
      </h1>

      {loading ? (
        <div className="view-news-loading">
          <div className="view-news-spinner"></div>
          <p>Loading the latest updates...</p>
        </div>
      ) : newsList.length === 0 ? (
        <div className="view-news-empty">
          <FaNewspaper className="view-news-empty-icon" />
          <h3>No News Available</h3>
          <p>There are currently no news articles to display. Check back later!</p>
        </div>
      ) : (
        <>
          {/* Hero / Latest News Section */}
          {latestNews && (
            <div className="view-news-hero" onClick={() => openNews(latestNews)}>
              <div className="view-news-hero-image-wrapper">
                <div className="view-news-hero-badge">Featured Update</div>
                {getImageUrl(latestNews) ? (
                  <img 
                    src={getImageUrl(latestNews)} 
                    alt={latestNews.title}
                    className="view-news-hero-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="view-news-placeholder" style={{ display: getImageUrl(latestNews) ? 'none' : 'flex' }}>
                  <FaNewspaper />
                </div>
              </div>
              
              <div className="view-news-hero-content">
                <div className="view-news-meta">
                  <FaCalendarAlt />
                  <span>{formatDate(latestNews.date)}</span>
                </div>
                <h2 className="view-news-hero-title">{latestNews.title}</h2>
                <p className="view-news-hero-preview">
                  {latestNews.content.substring(0, 180)}...
                </p>
                <button className="view-news-read-btn">
                  Read Full Story <FaChevronRight className="view-news-btn-icon" />
                </button>
              </div>
            </div>
          )}

          {/* Grid Section for All Other News */}
          <div className="view-news-grid-section">
            <h2 className="view-news-section-title">All Articles ({newsList.length})</h2>
            <div className="view-news-grid">
              {newsList.map((news) => (
                <div key={news._id} className="view-news-card" onClick={() => openNews(news)}>
                  <div className="view-news-card-img-wrapper">
                    {getImageUrl(news) ? (
                      <img 
                        src={getImageUrl(news)} 
                        alt={news.title}
                        className="view-news-card-img"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="view-news-placeholder" style={{ display: getImageUrl(news) ? 'none' : 'flex' }}>
                      <FaNewspaper />
                    </div>
                  </div>
                  <div className="view-news-card-info">
                    <div className="view-news-meta">
                      <FaCalendarAlt />
                      <span>{formatDate(news.date)}</span>
                    </div>
                    <h3 className="view-news-card-title">{news.title}</h3>
                    <p className="view-news-card-excerpt">
                      {news.content.substring(0, 100)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Full Screen Modal */}
      {selectedNews && (
        <div className="view-news-modal-overlay" onClick={closeNews}>
          <div className="view-news-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="view-news-modal-close" onClick={closeNews} title="Close">
              <FaTimes />
            </button>
            
            {/* Modal Image — only shown if image exists */}
            {getImageUrl(selectedNews) && (
              <div className="view-news-modal-cover">
                <img 
                  src={getImageUrl(selectedNews)} 
                  alt={selectedNews.title} 
                  onError={(e) => e.target.parentElement.style.display = 'none'}
                />
              </div>
            )}
            
            {/* Modal Content */}
            <div className="view-news-modal-content">
              <div className="view-news-modal-meta">
                <span className="view-news-modal-date">
                  <FaCalendarAlt style={{ marginRight: '6px' }} />
                  Published on {formatDate(selectedNews.date)}
                </span>
              </div>
              <h2 className="view-news-modal-title">{selectedNews.title}</h2>
              
              {/* white-space: pre-wrap ensures user's paragraph spacing is perfect */}
              <div className="view-news-modal-body">
                {selectedNews.content}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewNews;